import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import style from './FoodImageSlider.module.css';
import noImg from '../../../images/common/img_no_diet_02.png';


const LABEL_MARGIN = 0.1;   // 가장자리 여유 (0~1)
const clamp = (v) => Math.min(Math.max(Number(v) || 0, LABEL_MARGIN), 1 - LABEL_MARGIN);

const FoodImageSlider = ({ images = [], activeIndex = 0, onUpload, onSlideChange, loading = false, pending = false }) => {
    const paginationRef = useRef(null);

    const [swiperInst, setSwiperInst] = useState(null);

    // key -> { ar, fit } : 이미지 로드 후 원본 비율로 결정
    const [meta, setMeta] = useState({});

    // 개별 이미지 디코딩 완료 여부 — 슬라이드별 스켈레톤 해제용
    const [loadedKeys, setLoadedKeys] = useState({});

    // 이미지 로드 실패 — no-image 대체용
    const [errorKeys, setErrorKeys] = useState({});

    const handleImageLoad = useCallback((key) => (e) => {
        const el = e?.currentTarget ?? e;
        if (!el) return;

        const { naturalWidth: w, naturalHeight: h } = el;
        if (!w || !h) return;

        setMeta((prev) => {
            if (prev[key]) return prev;
            return {
                ...prev,
                // ar >= 1 (4:3, 1:1 등 가로 사이즈가 긴거) → cover / ar < 1 (2:3 등 세로 사이즈가 긴거) → contain
                [key]: { ar: w / h, fit: w >= h ? 'cover' : 'contain' },
            };
        });
        setLoadedKeys((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    }, []);

     const attachImageRef = useCallback((key) => (el) => {
        if (el?.complete && el.naturalWidth) handleImageLoad(key)(el);
    }, [handleImageLoad]);

    const handleImageError = useCallback((key) => (e) => {
        // e.currentTarget.style.visibility = 'hidden';
        // 실패해도 스켈레톤은 걷어낸다 — 무한 로딩 방지
        setLoadedKeys((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
        setErrorKeys((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    }, []);

    

    // useEffect(() => {
    //     const swiper = swiperRef.current;

    //     if (!swiper || swiper.destroyed) return;

    //     if (swiper.realIndex !== activeIndex) {
    //         swiper.slideTo(activeIndex, 0);
    //     }
    // }, [activeIndex, images.length]);

    useEffect(() => {
        if (!swiperInst || swiperInst.destroyed) return;

        if (swiperInst.realIndex !== activeIndex) {
            swiperInst.slideTo(activeIndex, 0);
        }
    }, [swiperInst, activeIndex, images.length]);



    useEffect(() => {
        const el = paginationRef.current;

        if (!swiperInst || swiperInst.destroyed || !el) return;

        if (swiperInst.params.pagination.el !== el) {
            swiperInst.params.pagination.el = el;
            swiperInst.pagination.destroy();
            swiperInst.pagination.init();
         }
        swiperInst.pagination.render();
            swiperInst.pagination.update();
        }, [swiperInst, images.length]);

    /* ── 데이터 로딩 중 ── */
    if (pending && !loading) {
        return (
            <div className={style.wrap}>
                <div className={style.slide} />
            </div>
        );
    }

    /* ── 로딩 중 ── */
    if (loading) {
        return (
            <div className={style.wrap}>
                <div className={style.slide}>
                    <div className={style.skeletonSlide}>
                    </div>
                </div>
                <div className={style.pagination}>
                    {[0, 1, 2].map((i) => (
                        <span key={i} className={style.skeletonBullet} />
                    ))}
                </div>
            </div>
        );
    }

    // 빈 상태
    if (!images || images.length === 0) {
        return (
            <div className={style.wrap}>
                <button
                    type="button"
                    className={style.emptySlot}
                    onClick={onUpload}
                    aria-label="식사 사진 추가"
                >
                    <span className={style.emptyIcon} aria-hidden="true" />
                    <p className={style.emptyTitle}>식사 사진 남기기</p>
                    <p className={style.emptyDesc}>탭하여 기록용 사진을 추가할 수 있어요</p>
                </button>
            </div>
        );
    }

    return (
        <div className={style.wrap}>
            
            <Swiper
                modules={[Pagination]}
                slidesPerView={1}
                observer={true}
                observeParents={true}
                initialSlide={activeIndex}
                pagination={{
                    el: paginationRef.current,
                    clickable: true,
                    bulletClass: style.bullet,
                    bulletActiveClass: style.bulletActive,
                }}
                // onBeforeInit={(swiper) => {
                //     swiper.params.pagination.el = paginationRef.current;
                // }}
                onSlideChange={(swiper) => onSlideChange?.(swiper.realIndex)}

                onSwiper={(swiper) => {
                    // swiperRef.current = swiper;
                    setSwiperInst(swiper);
                }}
            >
                {images.map((img, idx) => {

                    const key = img.fileId != null && img.fileSn != null
                                ? `${img.fileId}-${img.fileSn}`
                                : `${img.calorRecId}-${idx}`;
                    const info = meta[key];
                    const fit = info?.fit ?? 'contain';
                    const isImageLoaded = Boolean(loadedKeys[key]);
                    const isBroken = !img.imageUrl || Boolean(errorKeys[key]);

                    return (
                        <SwiperSlide key={key}>
                            {isBroken ? (
                                <div className={style.slide}>
                                    <div className={style.noImage}>
                                        <img src={noImg} alt="이미지 없음" className={style.noImageImg} />
                                    </div>
                                </div>
                            ) : (
                            <div className={style.slide} data-fit={fit}>

                                {/* 이미지 디코딩 전까지 슬라이드를 덮는 스켈레톤 */}
                                {!isImageLoaded && (
                                    <div className={style.skeletonSlide} />
                                )}
                                
                                <div
                                    className={style.backdrop}
                                    style={{ backgroundImage: `url(${img.imageUrl})` }}
                                    aria-hidden="true"
                                />

                                {/* 렌더 크기에 정확히 맞는 래퍼 = 라벨 좌표 기준 */}
                                <div
                                    className={style.imageBox}
                                    style={info ? { '--ar': String(info.ar) } : undefined}
                                >
                                    <img
                                        src={img.imageUrl}
                                        alt={`식단 이미지 ${idx + 1}`}
                                        className={style.image}
                                        draggable={false}
                                        ref={attachImageRef(key)}
                                        onLoad={handleImageLoad(key)}
                                        // onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                                        onError={handleImageError(key)}
                                    />
                                    {isImageLoaded && img.labels?.map((lb, lIdx) => (
                                        <span
                                            key={`${lb.name}_${lIdx}`}
                                            className={style.label}
                                            style={{
                                                left: `${clamp(lb.x) * 100}%`,
                                                top: `${clamp(lb.y) * 100}%`,
                                            }}
                                        >
                                            {lb.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            )}
                        </SwiperSlide>
                    );
                })}
            </Swiper>
             <div className={style.pagination} ref={paginationRef} aria-hidden="true" />
            
        </div>
    );
};

export default FoodImageSlider;